import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface SettingsListItemProps {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  iconBgColor?: string;
  iconBorderColor?: string;
  iconSize?: number;
  onPress?: () => void;
  isDestructive?: boolean;
  showChevron?: boolean;
  showSeparator?: boolean;
  disabled?: boolean;
}

export const SettingsListItem: React.FC<SettingsListItemProps> = React.memo(({
  title,
  subtitle,
  icon,
  iconBgColor,
  iconBorderColor,
  iconSize = 40,
  onPress,
  isDestructive = false,
  showChevron = true,
  showSeparator = true,
  disabled = false,
}) => {
  const { colors } = useThemeColors();
  const defaultIconBg = iconBgColor ?? colors.activityIcon.bg;
  const defaultIconBorder = iconBorderColor ?? colors.activityIcon.border;

  const content = (
    <View style={[styles.row, showSeparator && { borderBottomWidth: 1, borderBottomColor: colors.separator }]}>
      <View
        style={[
          styles.iconContainer,
          {
            width: iconSize,
            height: iconSize,
            borderRadius: 10,
            backgroundColor: defaultIconBg,
            borderColor: defaultIconBorder,
          },
        ]}
      >
        {icon}
      </View>
      <View style={styles.textContainer}>
        <Text
          style={[
            styles.title,
            { color: colors.text.tertiary },
            isDestructive && { color: colors.accent.red },
          ]}
          numberOfLines={2}
        >
          {title}
        </Text>
        {subtitle ? (
          <Text style={[styles.subtitle, { color: colors.text.dim }]} numberOfLines={2}>
            {subtitle}
          </Text>
        ) : null}
      </View>
      {showChevron && (
        <View style={styles.chevronContainer}>
          <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT
            width={16}
            height={16}
            color={colors.text.dimmer}
          />
        </View>
      )}
    </View>
  );

  if (onPress) {
    return (
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.7}
        disabled={disabled}
        style={disabled ? styles.disabled : undefined}
      >
        {content}
      </TouchableOpacity>
    );
  }

  return content;
});

SettingsListItem.displayName = 'SettingsListItem';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    // 73 = 40pt icon + 2×16 padding (+1 of slack); grows with two-line titles.
    minHeight: 73,
    paddingVertical: 16,
  },
  iconContainer: {
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  textContainer: {
    flex: 1,
    gap: 2,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.body,
    fontWeight: '500',
  },
  subtitle: {
    ...REDESIGN_TYPOGRAPHY.caption,
  },
  chevronContainer: {
    width: 16,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  disabled: {
    opacity: 0.5,
  },
});
